import { Component, Vue } from 'vue-property-decorator';
import ErrorHandler from '@/support/ErrorHandler';
import { getStatusColor, getStatusLabel, ReportStatus } from '@/support/ReportStatus';
import { AxiosError } from 'axios';
import { Application } from '@/support/Application';
import { Report as ReportModel } from '@/models/Report';
import { PusherService } from '@/support/PusherService';
import { userReportTabs, ReportTab } from '@/models/User';
import { ReportSyncFailed, ReportSyncFinished, ReportSyncStarted } from '@/support/Pusher/ReportSync';
import { MiDialogOptions } from '@/components/MiDialog/MiDialog';
import { openDialog } from '@/support/Dialog';
import Datepicker from '@/components/Datepicker/Datepicker.vue';
import DefaultDialog from '@/components/dialog/DefaultDialog.vue';
import DatePlanningBlock from '@/components/DatePlanningBlock/DatePlanningBlock.vue';
import PreparationPlanningBlock from '@/components/PreparationPlanningBlock/PreparationPlanningBlock.vue';
import { Contractor } from '@/models/Contractor';
import { DateFormatter } from '@/support/DateFormatter';
import { OverwriteDateRequest, UpdateDateRequest } from '@/components/DatePlanningInput/DatePlanningInput';

@Component<Report>({
  components: {
    Datepicker,
    DefaultDialog,
    DatePlanningBlock,
    PreparationPlanningBlock,
  },
})
export default class Report extends Vue {
  protected $pageTitle = 'Opdracht';

  protected ReportStatus = ReportStatus;

  protected report: ReportModel | null = null;

  protected isSyncing = false;

  protected isLoading = false;

  protected isLoadingReport = true;

  protected currentPath = '';

  protected activeTab = 'communication';

  protected reportTabs: ReportTab[] = userReportTabs;

  protected hasContractor = false;

  protected allContractors: Contractor[] = [];

  protected selectedContractor = '';

  protected showInRepairModal = false;

  protected isEditingContractorReview = false;

  protected isEditingProcedureReview = false;

  protected isAcceptingContract = false;

  protected reviewContractor: number | null = null;

  protected reviewProcedure: number | null = null;

  protected firstAppointment: DateFormatter = new DateFormatter();

  protected deliveryAppointment: DateFormatter = new DateFormatter();

  protected async mounted(): Promise<void> {
    await this.initialize();
    if (! this.hasContractor && this.$store.getters.isBureau) {
      await this.fetchAllContractors();
    }
    this.setActiveTab();
    this.initializePusher();
    this.isLoading = false;
  }

  protected async initialize(): Promise<void> {
    await this.fetchReport();
  }

  protected goToContractWord(): void {
    const url = this.report && this.report.contract_links ? this.report?.contract_links.docx : '';
    window.open(url, '_blank');
  }

  protected goToContractPdf(): void {
    const url = this.report && this.report.contract_links ? this.report?.contract_links.pdf : '';
    window.open(url);
  }

  protected goToContractor(id: string): void {
    this.$router.push(`/contractors/${id}`);
  }

  protected close(): void {
    this.showInRepairModal = false;
  }

  protected handleChangeStatus(newStatus: string): void {
    if (newStatus === 'hin_assignment_completed') {
      this.finishTasks();
    }
  }

  protected async acceptContract(): Promise<void> {
    this.isAcceptingContract = true;
    await this.report?.signContract()
    .then(async () => {
      this.showInRepairModal = false;
      this.isAcceptingContract = false;
      this.isLoading = true;
      await this.initialize();
      this.setActiveTab();
      this.initializePusher();
      this.isLoading = false;
    })
    .catch((error: AxiosError) => {
      ErrorHandler.network(error);
    });
  }

  protected finishTasks(): void {
    openDialog({
      title: 'Werkzaamheden gereed ',
      text: 'Weet je zeker dat je klaar bent met de werkzaamheden voor dit dossier?',
      type: 'warning',
      buttons: {
        cancel: { color: 'gray', text: 'Nee' },
        confirm: { color: 'success',
          text: 'Ja',
          action: async () => {
            if (! this.report) {
              return;
            }

            await this.updateReportStatus(ReportStatus.ASSIGMENT_COMPLETED)
              .then(() => {
                this.initialize();
              })
              .catch((error: AxiosError) => {
                ErrorHandler.network(error);
              });
          } },
      },
    });
  }

  protected startAssignment(): void {
    openDialog({
      title: 'Werkzaamheden starten ',
      text: `Weet je zeker dat je de status van dit dossier wilt veranderen naar ${getStatusLabel(ReportStatus.ASSIGMENT_STARTED)}?`,
      type: 'warning',
      buttons: {
        cancel: { color: 'gray', text: 'Nee' },
        confirm: { color: 'success',
          text: 'Ja',
          action: async () => {
            if (! this.report) {
              return;
            }

            await this.updateReportStatus(ReportStatus.ASSIGMENT_STARTED)
              .then(() => {
                this.initialize();
              })
              .catch((error: AxiosError) => {
                ErrorHandler.network(error);
              });
          } },
      },
    });
  }

  protected revertAssigmentStarted(): void {
    openDialog({
      title: 'Dossier terugzetten',
      text: `Weet je zeker dat je dit dossier wilt terugzetten naar de status ${getStatusLabel(ReportStatus.ASSIGMENT_ACCEPTED)}?`,
      type: 'warning',
      buttons: {
        cancel: { color: 'gray', text: 'Nee' },
        confirm: { color: 'success',
          text: 'Ja',
          action: async () => {
            if (! this.report) {
              return;
            }

            await this.updateReportStatus(ReportStatus.ASSIGMENT_ACCEPTED)
              .then(() => {
                this.initialize();
              })
              .catch((error: AxiosError) => {
                ErrorHandler.network(error);
              });
          } },
      },
    });
  }

  protected async fetchAllContractors(): Promise<void> {
    await new Contractor().limit(1000).all()
      .then((contractors: Contractor[]) => {
        this.allContractors = contractors;
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected async updateReportContractor(): Promise<void> {
    if (! this.report) {
      return;
    }

    const contractor = this.selectedContractor;

    await new ReportModel(this.report).include('contractor').update(
      { contractor },
    ).then(() => {
      this.initialize();
      this.$store.dispatch('openDialog', this.saveContractorSuccessDialogOptions);
    })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected async updateReportReview(key: string): Promise<void> {
    if (! this.report) {
      return;
    }

    let payload = {};

    if (key === 'review_contractor') {
      if (! this.isValidNumber(this.reviewContractor)) {
        return;
      }
      payload = { review_contractor: this.reviewContractor };
    }

    if (key === 'review_procedure') {
      if (! this.isValidNumber(this.reviewProcedure)) {
        return;
      }
      payload = { review_procedure: this.reviewProcedure };
    }

    await new ReportModel(this.report).update(
      payload,
    ).then(() => {
      if (key === 'review_contractor') {
        this.reviewContractor = null;
        this.isEditingContractorReview = false;
      }
      if (key === 'review_procedure') {
        this.reviewProcedure = null;
        this.isEditingProcedureReview = false;
      }
      this.initialize();
    })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected isValidNumber(value: number | null): boolean {
    if (! value) {
      return false;
    }

    if (value > 10 || value < 0) {
      return false;
    }

    return true;
  }

  protected copyReport(): void {
    this.$store.dispatch('openDialog', this.dialogOptionsCopy);
  }

  protected get dialogOptionsCopy(): MiDialogOptions {
    return {
      title: 'Bevestigen',
      text: 'Weet je zeker dat je een V2 wilt aanmaken van deze opdracht? De originele opdracht zal worden afgebroken.',
      type: 'warning',
      buttons: {
        cancel: {
          text: 'Annuleren',
          color: 'gray',
          action: () => {
            this.$store.dispatch('closeDialog', this.dialogOptionsCopy);
          },
        },
        confirm: {
          text: 'Aanmaken',
          color: 'primary',
          action: async () => {
            await this.createv2();
            await this.initialize();
            this.$store.dispatch('closeDialog', this.dialogOptionsCopy);
          },
        },
      },
    };
  }

  protected async createv2(): Promise<void> {
    this.isLoading = true;
    await this.report?.createv2();
    this.isLoading = false;
  }

  protected async fetchReport(): Promise<void> {
    if (! this.$route.params.id) {
      return;
    }
    this.isLoadingReport = true;

    this.report = null;

    await new ReportModel()
      .include(['damages', 'contractor', 'applicant', 'intermediary', 'contract_links'])
      .find(this.$route.params.id)
      .then((report: ReportModel) => {
        this.report = report;
        if (report?.contractor?.hasOwnProperty('uuid')) {
          this.hasContractor = true;
        }
        this.isLoadingReport = false;
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected async updateReportStatus(status: string): Promise<void> {
    await this.updateReport({ status });
  }

  protected async updateReport(payload: Record<string, any>): Promise<void> {
    if (! this.report) {
      return;
    }
    await new ReportModel(this.report).update(
      payload,
    ).then(() => {
      this.initialize();
    })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected async onUpdateDate(request: UpdateDateRequest): Promise<void> {
    const payload: Record<string, string> = {};

    payload[request.field] = `${request.date}`;
    await this.updateReport(payload);
  }

  protected async onOverwriteDate(request: OverwriteDateRequest): Promise<void> {
    const payload: Record<string, string> = {};

    payload[request.field] = `${request.event.date}`;
    payload[`${request.field}_change_reason`] = `${request.event.reason}`;

    await this.updateReport(payload);
  }

  protected setActiveTab(): void {
    const urlParts = window.location.pathname.split('/');
    this.currentPath = `/${urlParts[1]}/${urlParts[2]}`;

    if (! urlParts[3]) {
      this.$router.replace(`${this.currentPath}/communication`);
    } else {
      this.activeTab = window.location.pathname;
    }
  }

  protected getPillColor(status: string): string {
    return getStatusColor(status);
  }

  protected getPillLabel(status: string): string {
    return getStatusLabel(status);
  }

  protected get reportHasNonEditableStatus(): boolean {
    if (! this.report) {
      return false;
    }
    return ['hin_aborted', 'hin_closed'].includes(this.report.status_raw ? this.report.status_raw : '');
  }

  protected get deliveryDate(): string {
    if (! this.report) {
      return '';
    }
    const date = this.report?.delivery_appointment?.split(' ');
    return date ? date[0] : '';
  }

  protected get firstAppointmentDate(): string {
    if (! this.report) {
      return '';
    }
    const date = this.report?.first_appointment?.split(' ');
    return date ? date[0] : '';
  }

  protected get tabs(): Tab[] {
    return [
      {
        name: 'Communicatie',
        key: 'communication',
        href: '/communication',
        visible: () => true,
      },
      {
        name: 'Schades',
        key: 'damages',
        href: '/damages',
        visible: () => true,
      },
      {
        name: 'Bijlagen',
        key: 'attachments',
        href: '/attachments',
        visible: () => true,
      },
      {
        name: 'Begeleidende tekst',
        key: 'instructions',
        href: '/instructions',
        visible: () => true,
      },
      {
        name: 'Feedback',
        key: 'feedback',
        href: '/feedback',
        visible: () => this.$store.getters.isBureau,
      },
    ];
  }

  protected get updatableStatuses(): StatusItem[] {
    if (! this.report) {
      return [];
    }

    if (! this.report.status_raw) {
      return [];
    }

    if (this.report.status_raw === 'hin_preparation') {
      return [
        { key: 'hin_aborted', label: getStatusLabel('hin_aborted') },
        { key: 'hin_assignment_completed', label: getStatusLabel('hin_assignment_completed') },
      ];
    }

    if (this.report.status_raw === 'hin_assignment_started') {
      return [
        { key: 'hin_aborted', label: getStatusLabel('hin_aborted') },
        { key: 'hin_assignment_completed', label: getStatusLabel('hin_assignment_completed') },
      ];
    }

    if (this.report.status_raw === 'hin_assignment_completed') {
      return [
        { key: 'hin_closed', label: getStatusLabel('hin_closed') },
      ];
    }

    return [];
  }

  protected get canAccessContractEditor(): boolean {
    if (! this.report) {
      return false;
    }

    if (this.$store.getters.isContractor) {
      return false;
    }

    return this.report.status_raw === 'hin_preparation' || this.report.status_raw === 'hin_in_progress';
  }

  protected get saveContractorSuccessDialogOptions(): MiDialogOptions {
    return {
      text: 'Het geselecteerde herstelbedrijf is toegewezen',
      type: 'success',
      buttons: {
        confirm: {
          text: 'Sluiten',
          action: () => {
            this.$store.dispatch('closeDialog');
          },
        },
      },
    };
  }

  // PUSHER
  /**
   * Initalize Pusher room and listen for events
   */
  private pusher: PusherService | null = null;

  private initializePusher() {
    if (! Application.config('APP_APPLICATION_MODE') || ! this.$store.state.Auth) {
      return;
    }

    this.pusher = (new PusherService() as PusherService)
      .author(this.$store.state.Auth)
      .room(Application.config('APP_APPLICATION_MODE') as string)
      .echo()
      .on('report:sync.started', (event: ReportSyncStarted) => {
        this.handleSyncStarted(event.report);
      })
      .on('report:sync.finished', (event: ReportSyncFinished) => {
        this.handleSyncFinished(event.report);
      })
      .on('report:sync.failed', (event: ReportSyncFailed) => {
        this.handleSyncFailed(event.report, event.error);
      });
  }

  private handleSyncStarted(syncReport: ReportModel) {
    if (this.report && syncReport.uuid !== this.report.uuid) {
      return;
    }

    this.isSyncing = true;
    this.$root.$emit('syncReport', true);
  }

  private handleSyncFinished(syncReport: ReportModel) {
    if (this.report && syncReport.uuid !== this.report.uuid) {
      return;
    }

    this.isSyncing = false;
    this.$root.$emit('syncReport', false);
  }

  private handleSyncFailed(syncReport: ReportModel, error: string) {
    if (this.report && ((syncReport.uuid !== this.report.uuid) || ! error)) {
      return;
    }

    this.isSyncing = false;
    this.$root.$emit('syncReport', false);
  }
}

interface Tab {
  name: string;
  key: string;
  href: string;
  // eslint-disable-next-line @typescript-eslint/ban-types
  visible: Function;
}

type StatusItem = {
  label: string;
  key: string;
}
